import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Avatar,
  Typography,
  Box,
  CircularProgress,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  MenuItem,
} from "@mui/material";
import { motion } from "framer-motion";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";
import Swal from "sweetalert2";
import configServ from "../../services/config";
import { useSelector } from "react-redux";
import IOSSwitch from "../../Components/Buttons/IOSSwitch";
import { Edit, Delete } from "@mui/icons-material";
const ResourceMaster = () => {
  const [isCreate, setIsCreate] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { company_id, user_id } = useSelector((state) => state.GlobalVariables);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    about: "",
    web_url: "",
    active: true,
    image_base64: "",
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [isPodcasterName, setIsPodcasterName] = useState(false);
  const [podcastList, setPodcastList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("name"); // default search by name

  const handlePopup = () => {
    setIsCreate(!isCreate);
    setIsEdit(false);
    clear();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Remove validation error for that specific field when user types
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Image Upload + Base64 Conversion
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File too large",
        text: "Image size must be less than 1MB",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
      setFormData((prev) => ({ ...prev, image_base64: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const clear = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      about: "",
      web_url: "",
      active: true,
      image_base64: "",
    });
    setPreviewImage(null);
    setIsEdit(false);
    setValidationErrors({});
    setIsPodcasterName(false);
  };

  // API Call to check podcaster name
  const checkPodcasterName = async () => {
    try {
      const dataToSend = {
        company_id: company_id,
        name: formData.name,
      };
      const res = await configServ.checkResourceMasterName(dataToSend);
      if (res.status === 200) {
        setIsPodcasterName(true);
      } else {
        setIsPodcasterName(false);
      }
    } catch (e) {
      console.log(e);
      setIsPodcasterName(false);
    }
  };

  useEffect(() => {
    if (formData.name) {
      checkPodcasterName();
    } else {
      setIsPodcasterName(false);
    }
  }, [formData.name]);

  const handleSubmit = async () => {
    console.log("formData", formData);
    setValidationErrors({});
    if (!formData.name.trim()) {
      setValidationErrors({ name: "Name is required" });
      return;
    }

    if (!isPodcasterName && !isEdit) {
      setValidationErrors({ name: "ResourceMaster name already exists" });
      return;
    }

    try {
      let res;
      if (isEdit) {
        res = await configServ.updateResourceMaster(formData.id, {
          ...formData,
          company_id,
          updated_by: user_id,
        });
      } else {
        res = await configServ.createResourceMaster({
          ...formData,
          company_id,
          created_by: user_id,
        });
      }

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text:
            res.message ||
            (isEdit ? "ResourceMaster updated!" : "ResourceMaster created!"),
          timer: 2000,
          showConfirmButton: false,
        });
        clear();
        setIsCreate(false);
        fetchPodcasts();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Something went wrong!",
      });
    }
  };

  const onEdit = (item) => {
    console.log("onEdit=", item);
    setIsCreate(true);
    setIsEdit(true);
    setFormData({
      id: item.id,
      company_id: item.company_id,
      name: item.name || "",
      email: item.email || "",
      phone: item.phone || "",
      about: item.about || "",
      web_url: item.web_url || "",
      active: item.active === 1,
      image_base64: item.image_base64 || "", // Will be updated if needed
    });
    setPreviewImage(item.image_base64);
  };

  const fetchPodcasts = async () => {
    try {
      setLoading(true);
      const res = await configServ.getResourceMasterByCompany(company_id);
      if (res.success) {
        setPodcastList(res.data); // make sure you have state: const [podcastList, setPodcastList] = useState([]);
      } else {
        console.error("Failed to fetch podcasts:", res.data.message);
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 useEffect only calls the function
  useEffect(() => {
    if (company_id) {
      fetchPodcasts();
    }
  }, [company_id]);

  return (
    <>
      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"Resource Master Entry"}
        handlePopup={handlePopup}
        create={isCreate}
        isEdit={isEdit}
      />

      {isCreate && (
        <motion.div
          style={{
            width: "100%",
            backgroundColor: "#e3f3fc",
            padding: 20,
            borderRadius: "10px",
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.1, delay: 0.1 }}
        >
          <Grid container spacing={2}>
            {/* Name */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label={
                  <span>
                    Name<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="name"
                onChange={handleChange}
                value={formData.name || ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isPodcasterName || isEdit ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </InputAdornment>
                  ),
                }}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                name="email"
                onChange={handleChange}
                value={formData.email || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                name="phone"
                onChange={handleChange}
                value={formData.phone || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            {/* About */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="About"
                name="about"
                multiline
                rows={3}
                onChange={handleChange}
                value={formData.about || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            {/* Image Upload */}
            <Grid item xs={12} sm={6} md={4}>
              <Button variant="outlined" component="label" fullWidth>
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
              </Button>
              {previewImage && (
                <Avatar
                  src={previewImage}
                  alt="Preview"
                  sx={{ width: 80, height: 80, mt: 1 }}
                />
              )}
              <Typography variant="caption" color="textSecondary">
                Max size: 1MB
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Web url"
                name="web_url"
                onChange={handleChange}
                value={formData.web_url || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            {/* Active Status */}
            <Grid item xs={12} md={3}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={formData.active}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          active: e.target.checked,
                        }))
                      }
                      name="active"
                    />
                  }
                  label="Active"
                />
              </FormGroup>
            </Grid>
          </Grid>

          {/* Buttons */}
          <Grid container spacing={2} justifyContent="flex-end" mt={2}>
            <Grid item>
              <Button
                onClick={clear}
                variant="contained"
                color="secondary"
                startIcon={<RotateLeftIcon />}
              >
                Reset
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color={isEdit ? "success" : "primary"}
                startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </motion.div>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 2,
          width: "100%",
          gap: 1,
        }}
      >
        <TextField
          select
          size="small"
          value={searchField}
          onChange={(e) => setSearchField(e.target.value)}
          sx={{ minWidth: 120, backgroundColor: "white" }}
        >
          <MenuItem value="name">Name</MenuItem>
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="phone">Phone</MenuItem>
        </TextField>

        <TextField
          size="small"
          placeholder={`Search by ${searchField}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 250, backgroundColor: "white" }}
        />
      </Box>

      {/* 🔹 Mobile Card View */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : podcastList.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}>
          No podcasts found
        </Box>
      ) : (
        <>
          {/* 🔹 Mobile Card View */}
          <Box
            sx={{ mt: 2, width: "100%", display: { xs: "block", sm: "none" } }}
          >
            <Grid container spacing={2}>
              {podcastList
                .filter((item) => {
                  const value = (item[searchField] || "")
                    .toString()
                    .toLowerCase();
                  return value.includes(searchQuery.toLowerCase());
                })
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((podcast, index) => (
                  <Grid item xs={12} key={podcast.id}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        position: "relative",
                        backgroundColor: "#fff",
                      }}
                    >
                      {/* Edit & Delete */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <IconButton
                          color="primary"
                          onClick={() => onEdit(podcast)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>

                        {/* <IconButton color="error">
                          <Delete fontSize="small" />
                        </IconButton> */}
                      </Box>

                      {/* Avatar */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <Avatar
                          src={podcast.image_base64}
                          alt={podcast.name}
                          sx={{
                            width: 80,
                            height: 80,
                            border: "2px solid #eee",
                          }}
                        />
                      </Box>

                      {/* Details */}
                      <Typography variant="h6" align="center" fontWeight="bold">
                        {podcast.name}
                      </Typography>
                      {podcast.email && (
                        <Typography
                          variant="body2"
                          align="center"
                          color="textSecondary"
                        >
                          {podcast.email}
                        </Typography>
                      )}
                      {podcast.phone && (
                        <Typography
                          variant="body2"
                          align="center"
                          color="textSecondary"
                        >
                          {podcast.phone}
                        </Typography>
                      )}
                      {podcast.web_url && (
                        <Typography
                          variant="body2"
                          align="center"
                          color="textSecondary"
                          sx={{
                            maxWidth: 200, // limit width
                            mx: "auto", // center horizontally
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap", // single line truncate for URL
                            display: "block",
                          }}
                          title={podcast.web_url} // show full URL on hover
                        >
                          {podcast.web_url}
                        </Typography>
                      )}

                      {podcast.about && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            textAlign: "center",
                            color: "text.secondary",
                            maxWidth: 220, // fixed width for layout
                            mx: "auto", // center horizontally
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2, // show only 2 lines
                            WebkitBoxOrient: "vertical",
                            whiteSpace: "normal", // allow wrapping for 2 lines
                          }}
                          title={podcast.about} // show full text on hover
                        >
                          {podcast.about}
                        </Typography>
                      )}

                      <Chip
                        label={podcast.active ? "Active" : "Inactive"}
                        color={podcast.active ? "success" : "default"}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Paper>
                  </Grid>
                ))}
            </Grid>
            <TablePagination
              component="div"
              count={podcastList.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>

          {/* 🔹 Desktop Table View */}
          <Box
            sx={{ mt: 2, width: "100%", display: { xs: "none", sm: "block" } }}
          >
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: "#1976d2" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff" }}>S.No.</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Profile</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Name</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Email</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Phone</TableCell>
                    <TableCell sx={{ color: "#fff" }}>About</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Url</TableCell>

                    <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                    <TableCell sx={{ color: "#fff" }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {podcastList
                    .filter((item) => {
                      const value = (item[searchField] || "")
                        .toString()
                        .toLowerCase();
                      return value.includes(searchQuery.toLowerCase());
                    })
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((podcast, index) => (
                      <TableRow key={podcast.id}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Avatar
                            src={podcast.image_base64}
                            alt={podcast.name}
                            sx={{ width: 50, height: 50 }}
                          />
                        </TableCell>
                        <TableCell>{podcast.name}</TableCell>
                        <TableCell>{podcast.email}</TableCell>
                        <TableCell>{podcast.phone}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200, // fixed column width
                            whiteSpace: "normal", // allow wrapping
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box", // needed for multiline truncation
                            WebkitLineClamp: 2, // show only 2 lines
                            WebkitBoxOrient: "vertical", // vertical orientation
                          }}
                        >
                          {podcast.about || "-"}
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap", // single line only
                          }}
                        >
                          {podcast.web_url || "-"}
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={podcast.active ? "Active" : "Inactive"}
                            color={podcast.active ? "success" : "default"}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => onEdit(podcast)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>

                          {/* <IconButton color="error">
                            <Delete fontSize="small" />
                          </IconButton> */}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={podcastList.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </Box>
        </>
      )}
    </>
  );
};

export default ResourceMaster;
